import miaFlowAuth from '../auth/MiaFlowAuth';

/**
 * Where a project opens when it is picked (from Home, the Projects list, a recent or favorite): its Dashboard, whatever
 * the project holds, with the conversation it was opened from when there is one.
 */
export function projectHomeUrl(tenantId, projectId, { conversationId } = {}) {
    const conversation = conversationId ? `conversationId=${encodeURIComponent(conversationId)}&` : '';
    return `/code/${tenantId}/${projectId}?${conversation}view=dashboard`;
}

/**
 * Where one file of a project opens in Code (a notification's link: a comment's line, a suggestion's file, a handoff's
 * last changed file). `filePath` is the project path (`services/<svc>/<path>`); `line` scrolls there (`#L<n>`). A path
 * outside a service opens the Dashboard.
 */
export function projectFileUrl(tenantId, projectId, filePath, line) {
    const parts = String(filePath || '').split('/').filter(Boolean);
    if (parts.length < 3 || parts[0] !== 'services') { return projectHomeUrl(tenantId, projectId); }
    const service = `services/${parts[1]}`;
    const rest = parts.slice(2).join('/');
    const hash = Number.isInteger(line) && line > 0 ? `#L${line}` : '';
    return `/code/${tenantId}/${projectId}/${encodeURIComponent(service)}/main/code/${encodeURIComponent(rest)}${hash}`;
}

/** A service name that can be put in a URL path as it is. */
export function isServiceSegment(name) {
    return typeof name === 'string' && name.trim() !== '' && name !== '.' && name !== '..';
}

/**
 * Where a project's app opens: the Preview of one of its services, with the conversation it was opened from when there is
 * one. For the chat's "Open the project" button once something has been built, where the Dashboard would only be in the way.
 */
export function projectPreviewUrl(tenantId, projectId, serviceName, { conversationId } = {}) {
    // `.` is the project root and `..`/empty are not services: in a path they would be dropped (or collapse a segment), shifting
    // every later segment one place left, so "main" would be read as the service. No real service named, the Dashboard.
    if (!isServiceSegment(serviceName)) { return projectHomeUrl(tenantId, projectId, { conversationId }); }
    const conversation = conversationId ? `?conversationId=${encodeURIComponent(conversationId)}` : '';
    return `/code/${tenantId}/${projectId}/${encodeURIComponent(serviceName)}/main/preview${conversation}`;
}

/**
 * Open a project in Code exactly as picking it from the project list does: remember it as the selected project, clear
 * the state kept for the previous project, flag the switch so the URL is not rebuilt, and go to its page. Used by the
 * list (App.jsx `handleSelectProjectFromHome`) and by the "Open the project" button the chat offers once an app is ready
 * (which names the service to preview).
 *
 * `conversationId` (optional) keeps the current chat conversation when it is opened.
 * Returns false when the project cannot be opened (no id).
 */
export function openProject({ project, navigate, conversationId, serviceName, onInvalid }) {
    const projectId = project?._id || project?.projectId || project?.id;
    if (!projectId) {
        onInvalid?.();
        return false;
    }

    miaFlowAuth.setSelectedProject({ projectId, name: project.name, description: project.description });
    const tenantId = project.tenantId || miaFlowAuth.getSelectedTenant()?.tenantId;

    if (tenantId) {
        localStorage.removeItem('mia_selectedResource');
        localStorage.removeItem('mia_selectedRevision');
        localStorage.removeItem('mia_activeTab');
        localStorage.removeItem('mia_fullProjectConfig');
        localStorage.removeItem('mia_selectedSession');
        // Prevents URL reconstruction while the switch is in flight.
        sessionStorage.setItem('mia_switching_to_project', projectId);
        // With a service named (the app that was just built) the project opens on its Preview; otherwise on its Dashboard.
        navigate(isServiceSegment(serviceName)
            ? projectPreviewUrl(tenantId, projectId, serviceName, { conversationId })
            : projectHomeUrl(tenantId, projectId, { conversationId }));
        return true;
    }

    // No tenant known: the old behaviour, handled by the project selector.
    navigate('/code');
    setTimeout(() => {
        window.dispatchEvent(new CustomEvent('select-project', { detail: { project, projectId } }));
    }, 100);
    return true;
}
